import React, { useEffect, useState } from "react";

import {
  Button,
  Card,
  Col,
  DatePicker,
  Dropdown,
  Form,
  Input,
  Menu,
  message,
  Modal,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import fileDownload from "js-file-download";
import PropTypes from "prop-types";

import {
  DownloadOutlined,
  FilterOutlined,
  ReloadOutlined,
} from "@ant-design/icons";

import moment from "moment";
import BulkPayoutModal from "../../Components/BulkPayoutModal/BulkPayoutModal";
import BulkRevenueShareModal from "../../Components/BulkRevenueShareModal";
import ManageSuperPartnerModal from "../../Components/ManageSuperPartnerModal";
import RefernEarnAddEditProductModal from "../../Components/RefernEarnAddEditProductModal";
import RefernEarnAddProductMultipleUsersModal from "../../Components/RefernEarnAddProductMultipleUsersModal";
import RefernEarnPartnerDetailsExpand from "../../Components/RefernEarnPartnerDetailsExpand";
import UpdateRevenueShareModal from "../../Components/UpdateRevenueShareModal";
import useDebounce from "../../hooks/useDebounce";
import {
  createRefernEarnPayoutToPartner,
  getLastMigrationStats,
  getRefernEarnPartners,
  suspendPartner,
  unSuspendPartner,
} from "../api";

const formatIndianNumber = (value) => {
  return new Intl.NumberFormat("en-IN").format(value);
};

const PartnersList = ({ setCurrentScreen }) => {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({
    limit: "", // onetime | subscription | onetime-subscription
    page: 1,
    pageSize: 10,
    search: "",
    startDate: "",
    endDate: "",
    minProductValue: "",
    maxProductValue: "",
    minEarnings: "",
    maxEarnings: "",
    minPayout: "",
    maxPayout: "",
    minPendingCommission: "",
    maxPendingCommission: "",
    superPartner: "all", // all | enabled | disabled
  });
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [multipleUsersModalVisible, setMultipleUsersModalVisible] =
    useState(false);
  const [bulkPayoutModalVisible, setBulkPayoutModalVisible] = useState(false);
  const [bulkRevenueShareModalVisible, setBulkRevenueShareModalVisible] =
    useState(false);
  const [revenueShareModalVisible, setRevenueShareModalVisible] =
    useState(false);
  const [filterModalVisible, setFitlerModalVisible] = useState(false);
  const [payoutModalVisible, setPayoutModalVisible] = useState(false);
  const [mode, setMode] = useState("add");
  const [product, setProduct] = useState(null);
  const [form] = Form.useForm();
  const [payoutForm] = Form.useForm();
  const [currentUserForPayout, setCurrentUserForPayout] = useState(null);
  const [currentPartnerForRevenueShare, setCurrentPartnerForRevenueShare] =
    useState(null);
  const [superPartnerModalVisible, setSuperPartnerModalVisible] =
    useState(false);
  const [currentPartnerForSuperPartner, setCurrentPartnerForSuperPartner] =
    useState(null);
  const [lastMigrationStats, setLastMigrationStats] = useState(null);

  const debouncedSearch = useDebounce({
    value: search,
    callback: () => {},
  });
  const [tableKey, setTableKey] = useState(Date.now());

  const handleGetLastMigrationStats = async () => {
    setLoading(true);

    try {
      const res = await getLastMigrationStats();

      if (res?.code === 200) {
        if (res.result.lastProductMigration) {
          setLastMigrationStats(res.result.lastProductMigration);
        }
      } else {
        message.error("Something went wrong");
      }
    } catch (err) {
      console.log(err);
      message.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleGetRefernEarnPartners = async (csv = false) => {
    // dont generate csv if startDate and endDate are not present
    if (csv && (!filters.startDate || !filters.endDate)) {
      message.error("Please select start and end date");
      return;
    }
    // dont generate csv if startDate and endDate are more than 3 months apart
    if (
      csv &&
      moment(filters.startDate).diff(moment(filters.endDate), "months") > 3
    ) {
      message.error("Start and end date should be less than 3 months apart");
      return;
    }

    const csvFilters = { ...filters };
    delete csvFilters.minProductValue;
    delete csvFilters.maxProductValue;
    delete csvFilters.minEarnings;
    delete csvFilters.maxEarnings;
    delete csvFilters.minPayout;
    delete csvFilters.maxPayout;
    delete csvFilters.minPendingCommission;
    delete csvFilters.maxPendingCommission;

    setLoading(true);
    let hide;
    if (csv) {
      hide = message.loading("Generating CSV...", 0);
    }
    try {
      const res = await getRefernEarnPartners(csv ? csvFilters : filters, csv);

      if (csv) {
        fileDownload(res, "refern_earn_partners.csv");
      } else if (res?.code === 200) {
        setData(res.result.partners.map((item, index) => ({ ...item, index })));
        setTotal(res.result.totalPartners);
        setTableKey(Date.now());
      } else {
        message.error("Something went wrong");
      }
    } catch (err) {
      console.log(err);
      message.error("Something went wrong");
    } finally {
      if (hide) hide();
      setLoading(false);
    }
  };

  const handleCreateRefernEarnPayoutForPartner = async (values, id) => {
    setLoading(true);
    try {
      const res = await createRefernEarnPayoutToPartner(values, id);
      if (res?.code === 201 || res?.code === 200) {
        payoutForm.resetFields();
        setPayoutModalVisible(false);
        handleGetRefernEarnPartners();
      } else {
        message.error(res?.message || "Something went wrong");
      }
    } catch (err) {
      console.log(err);
      if (err?.message) {
        message.error(err.message);
      } else {
        message.error("Something went wrong");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSuspendRefernEarnPartner = async (id) => {
    setLoading(true);
    try {
      const res = await suspendPartner(id);
      if (res?.code === 201) {
        message.success("Partner suspended successfully");
        handleGetRefernEarnPartners();
      } else {
        message.error(res?.message || "Something went wrong");
      }
    } catch (err) {
      console.log(err);
      if (err?.message) {
        message.error(err.message);
      } else {
        message.error("Something went wrong");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUnSuspendRefernEarnPartner = async (id) => {
    setLoading(true);
    try {
      const res = await unSuspendPartner(id);
      if (res?.code === 201) {
        message.success("Partner un-suspended successfully");
        handleGetRefernEarnPartners();
      } else {
        message.error(res?.message || "Something went wrong");
      }
    } catch (err) {
      console.log(err);
      if (err?.message) {
        message.error(err.message);
      } else {
        message.error("Something went wrong");
      }
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: "Partner",
      dataIndex: "name",
      key: "name",
      render: (_, record) => (
        <Space size="small" direction="vertical">
          <Space size="small">
            <Typography style={{}}>{record?.name}</Typography>
            {record?.isReferEarnSuperPartner ? (
              <Tooltip title="Super Partner">
                <Tag color="gold">Super</Tag>
              </Tooltip>
            ) : null}
          </Space>
          <Typography>{record?.email}</Typography>
          <Typography.Text
            type="secondary"
            style={{ fontSize: 12 }}
            copyable={{ text: record?._id }}
          >
            Referral ID: {record?._id}
          </Typography.Text>
          <Typography>{record?.phone}</Typography>
          <Typography>({record?.host})</Typography>
        </Space>
      ),
    },
    {
      title: "Total Product Value",
      dataIndex: "totalProductValue",
      key: "totalProductValue",
      defaultSortOrder: null,
      sortDirections: ["descend", "ascend"],
      sorter: (a, b) => a.totalProductValue - b.totalProductValue,
      render: (value) => (
        <Statistic
          value={value}
          precision={0}
          formatter={formatIndianNumber}
          valueStyle={{ fontSize: 16 }}
          prefix={<span className="siteInr">&#8377;</span>}
        />
      ),
    },
    {
      title: "Commission Earned",
      dataIndex: "totalEarnings",
      key: "totalEarnings",
      defaultSortOrder: null,
      sortDirections: ["descend", "ascend"],
      sorter: (a, b) => a.totalEarnings - b.totalEarnings,
      render: (value) => (
        <Statistic
          value={value}
          precision={0}
          formatter={formatIndianNumber}
          valueStyle={{ fontSize: 16 }}
          prefix={<span className="siteInr">&#8377;</span>}
        />
      ),
    },
    {
      title: "Total Payout",
      dataIndex: "totalPayout",
      key: "totalPayout",
      defaultSortOrder: null,
      sortDirections: ["descend", "ascend"],
      sorter: (a, b) => a.totalPayout - b.totalPayout,
      render: (value) => (
        <Statistic
          value={value}
          precision={0}
          formatter={formatIndianNumber}
          valueStyle={{ fontSize: 16 }}
          prefix={<span className="siteInr">&#8377;</span>}
        />
      ),
    },
    {
      title: "Pending Commissions",
      dataIndex: "pendingCommission",
      key: "pendingCommission",
      defaultSortOrder: null,
      sortDirections: ["descend", "ascend"],
      sorter: (a, b) => a.pendingCommission - b.pendingCommission,
      render: (value) => (
        <Statistic
          value={value}
          precision={0}
          formatter={formatIndianNumber}
          valueStyle={{ fontSize: 16 }}
          prefix={<span className="siteInr">&#8377;</span>}
        />
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Dropdown.Button
            type="primary"
            overlay={
              <Menu>
                <Menu.Item
                  onClick={() => {
                    setCurrentUserForPayout(record);
                    setPayoutModalVisible(true);
                  }}
                >
                  Manage Payout
                </Menu.Item>
                <Menu.Item
                  onClick={() => {
                    setCurrentPartnerForRevenueShare(record);
                    setRevenueShareModalVisible(true);
                  }}
                >
                  Update Revenue Share
                </Menu.Item>
                <Menu.Item
                  disabled={record.isReferEarnSuspended}
                  onClick={() => {
                    setMode("add");
                    setModalVisible(true);
                    setProduct({
                      user: {
                        _id: record?._id,
                        name: record?.name,
                        email: record?.email,
                        phone: record?.phone,
                        host: record?.host,
                      },
                    });
                  }}
                >
                  Add Product
                </Menu.Item>

                <Menu.Item
                  onClick={() =>
                    record.isReferEarnSuspended
                      ? handleUnSuspendRefernEarnPartner(record._id)
                      : handleSuspendRefernEarnPartner(record._id)
                  }
                >
                  {record.isReferEarnSuspended
                    ? "Unsuspend Partner"
                    : "Suspend Partner"}
                </Menu.Item>

                <Menu.Item
                  onClick={() => {
                    setCurrentPartnerForSuperPartner(record);
                    setSuperPartnerModalVisible(true);
                  }}
                >
                  {record.isReferEarnSuperPartner
                    ? "Remove Super Partner"
                    : "Mark as Super Partner"}
                </Menu.Item>
              </Menu>
            }
          />
        </Space>
      ),
    },
  ];

  useEffect(() => {
    handleGetRefernEarnPartners();
  }, [filters]);

  useEffect(() => {
    handleGetLastMigrationStats();
  }, []);

  useEffect(() => {
    if (debouncedSearch) {
      setFilters((prev) => ({
        ...prev,
        search: debouncedSearch,
        page: 1,
      }));
    }
  }, [debouncedSearch]);

  return (
    <>
      <Row gutter={6}>
        <Col span={24}>
          <Input
            minLength={2}
            className="searchbar"
            value={search}
            placeholder="Search by partner name, email or mobile"
            onChange={(event) => {
              if (event.target.value.length === 0) {
                setFilters({
                  ...filters,
                  search: "",
                });
              }
              setSearch(event.target.value);
            }}
          />
        </Col>
      </Row>

      <Row gutter={6} style={{ marginTop: "10px" }}>
        <Col span={24}>
          <Space>
            <DatePicker.RangePicker
              value={[filters.startDate, filters.endDate]}
              onChange={(dates) => {
                setFilters({
                  ...filters,
                  startDate: dates?.[0].startOf("day"),
                  endDate: dates?.[1].endOf("day"),
                });
              }}
              allowClear
            />
            <Button
              type="default"
              onClick={() => {
                // keep the modal in sync with the filter that is actually applied
                form.setFieldsValue({ superPartner: filters.superPartner });
                setFitlerModalVisible(true);
              }}
            >
              <FilterOutlined />
            </Button>
            <Button
              type="primary"
              onClick={() => {
                handleGetRefernEarnPartners(true);
              }}
            >
              Export CSV
            </Button>
            <Button
              type="primary"
              onClick={() => {
                setMultipleUsersModalVisible(true);
              }}
            >
              Add product for multiple users
            </Button>
            <Button
              type="primary"
              onClick={() => {
                setBulkPayoutModalVisible(true);
              }}
            >
              Manage Payouts
            </Button>
            <Button
              type="primary"
              onClick={() => {
                setBulkRevenueShareModalVisible(true);
              }}
            >
              Manage Revenue Share
            </Button>
          </Space>
        </Col>
      </Row>

      {lastMigrationStats ? (
        <Row gutter={[16, 16]} style={{ marginTop: "20px" }}>
          <Col span={12}>
            <Card>
              <Row justify="space-between" align="middle">
                <Col>
                  <Space>
                    <Typography.Text strong>Import Status:</Typography.Text>
                    <Typography.Text>
                      {lastMigrationStats?.noOfCompleted}/
                      {lastMigrationStats?.totalRecords} users imported
                    </Typography.Text>
                    {lastMigrationStats?.noOfErrors > 0 && (
                      <Typography.Text type="danger">
                        ({lastMigrationStats?.noOfErrors} failed)
                      </Typography.Text>
                    )}
                  </Space>
                </Col>
                <Col style={{ marginTop: "10px" }}>
                  <Space>
                    <Button
                      icon={<ReloadOutlined />}
                      onClick={() => {
                        handleGetLastMigrationStats();
                      }}
                    >
                      Refresh
                    </Button>
                    {lastMigrationStats?.errorList?.length > 0 && (
                      <Button
                        icon={<DownloadOutlined />}
                        onClick={() => {
                          const errorList = lastMigrationStats?.errorList || [];

                          // generating heading and error list string format with new line
                          const heading = "email,phone,host,reason\n";
                          const errorListString = errorList
                            .map(
                              (item) =>
                                `"${item.email}","${item.phone}","${item.host}","${item.errorMessage}"`
                            )
                            .join("\n");

                          // attaching heading and error list string to make the csv file structure.
                          const completeCsvContent = heading + errorListString;

                          // initiating file download with the complete csv content.
                          fileDownload(completeCsvContent, "error_cases.csv");
                        }}
                      >
                        Download error cases
                      </Button>
                    )}
                  </Space>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      ) : null}

      <Table
        key={tableKey}
        columns={columns}
        dataSource={data}
        loading={loading}
        style={{
          marginTop: "20px",
        }}
        pagination={{
          current: filters.page,
          total,
          pageSize: filters.pageSize,
          onChange: (p) => setFilters({ ...filters, page: p }),
        }}
        expandable={{
          expandedRowRender: (record) => (
            <RefernEarnPartnerDetailsExpand
              userDetails={record}
              setCurrentScreen={setCurrentScreen}
            />
          ),
        }}
        rowKey={(record) => record._id}
      />

      <RefernEarnAddEditProductModal
        mode={mode}
        product={product}
        visible={modalVisible}
        onClose={() => setModalVisible(false)}
        onSave={() => {
          handleGetRefernEarnPartners();
          setModalVisible(false);
        }}
      />

      <RefernEarnAddProductMultipleUsersModal
        visible={multipleUsersModalVisible}
        onClose={() => setMultipleUsersModalVisible(false)}
        onSave={() => setMultipleUsersModalVisible(false)}
      />

      <BulkPayoutModal
        visible={bulkPayoutModalVisible}
        onClose={() => setBulkPayoutModalVisible(false)}
        onSuccess={() => {
          handleGetRefernEarnPartners();
        }}
      />

      <BulkRevenueShareModal
        visible={bulkRevenueShareModalVisible}
        onClose={() => setBulkRevenueShareModalVisible(false)}
        onSuccess={() => {
          handleGetRefernEarnPartners();
        }}
      />

      <UpdateRevenueShareModal
        visible={revenueShareModalVisible}
        onClose={() => {
          setRevenueShareModalVisible(false);
          setCurrentPartnerForRevenueShare(null);
        }}
        partner={currentPartnerForRevenueShare}
        onSuccess={() => {
          handleGetRefernEarnPartners();
        }}
      />

      <ManageSuperPartnerModal
        visible={superPartnerModalVisible}
        onClose={() => {
          setSuperPartnerModalVisible(false);
          setCurrentPartnerForSuperPartner(null);
        }}
        partner={currentPartnerForSuperPartner}
        onSuccess={() => {
          handleGetRefernEarnPartners();
        }}
      />

      <Modal
        title="Filter Partners"
        visible={filterModalVisible}
        onCancel={() => {
          setFitlerModalVisible(false);
        }}
        destroyOnClose
        footer={[
          <Row>
            <Button
              key="reset"
              type="default"
              onClick={() => {
                form.resetFields();
              }}
              style={{ flex: 1 }}
            >
              Reset
            </Button>
            <Button
              key="submit"
              type="primary"
              loading={loading}
              onClick={() => {
                form.validateFields().then((values) => {
                  // find if oneTimeFee or subscriptionFee is present
                  // if yes then convert it to number

                  const validatedValues = values;

                  // Check for the below fields
                  // minProductValue
                  // maxProductValue
                  // minEarnings
                  // maxEarnings
                  // minPayout
                  // maxPayout
                  // minPendingCommission
                  // maxPendingCommission

                  // if present then check if they are numbers
                  // if min and max both are present then check if min < max

                  const keys = [
                    "minProductValue",
                    "maxProductValue",
                    "minEarnings",
                    "maxEarnings",
                    "minPayout",
                    "maxPayout",
                    "minPendingCommission",
                    "maxPendingCommission",
                  ];

                  keys.forEach((key) => {
                    if (
                      validatedValues[key] &&
                      Number.isNaN(Number(validatedValues[key]))
                    ) {
                      console.log("yoo", key);
                      message.error(`${key} should be a number`);
                    }
                  });

                  if (
                    validatedValues.minProductValue &&
                    validatedValues.maxProductValue &&
                    validatedValues.minProductValue >=
                      validatedValues.maxProductValue
                  ) {
                    message.error(
                      "Min Product Value should be less than Max Product Value"
                    );
                    return;
                  }

                  if (
                    validatedValues.minEarnings &&
                    validatedValues.maxEarnings &&
                    validatedValues.minEarnings >= validatedValues.maxEarnings
                  ) {
                    message.error(
                      "Min Earnings should be less than Max Earnings"
                    );
                    return;
                  }

                  if (
                    validatedValues.minPayout &&
                    validatedValues.maxPayout &&
                    validatedValues.minPayout >= validatedValues.maxPayout
                  ) {
                    message.error("Min Payout should be less than Max Payout");
                    return;
                  }

                  if (
                    validatedValues.minPendingCommission &&
                    validatedValues.maxPendingCommission &&
                    validatedValues.minPendingCommission >=
                      validatedValues.maxPendingCommission
                  ) {
                    message.error(
                      "Min Pending Commission should be less than Max Pending Commission"
                    );
                    return;
                  }

                  setFilters({
                    ...filters,
                    ...validatedValues,
                    page: 1,
                  });
                  setFitlerModalVisible(false);
                });
              }}
              style={{ flex: 1 }}
            >
              Apply
            </Button>
          </Row>,
        ]}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="superPartner"
            label="Super Partner"
            initialValue="all"
          >
            <Select>
              <Select.Option value="enabled">Enabled</Select.Option>
              <Select.Option value="disabled">Disabled</Select.Option>
              <Select.Option value="all">All</Select.Option>
            </Select>
          </Form.Item>

          <Typography>Total Product Value</Typography>
          <Row style={{ gap: "16px" }}>
            <Form.Item
              style={{ flex: 1 }}
              name="minProductValue"
              label="Min"
              rules={[
                {
                  validator: (_, value) => {
                    if (value && Number.isNaN(Number(value))) {
                      return Promise.reject(
                        new Error("Min Product Value should be a number")
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input placeholder="e.g. 1000" type="number" />
            </Form.Item>
            <Form.Item
              style={{ flex: 1 }}
              name="maxProductValue"
              label="Max"
              rules={[
                {
                  validator: (_, value) => {
                    if (value && Number.isNaN(Number(value))) {
                      return Promise.reject(
                        new Error("Max Product Value should be a number")
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input placeholder="e.g. 1000" type="number" />
            </Form.Item>
          </Row>

          <Typography>Commission Earning</Typography>
          <Row style={{ gap: "16px" }}>
            <Form.Item
              style={{ flex: 1 }}
              name="minCommissionEarning"
              label="Min"
              rules={[
                {
                  validator: (_, value) => {
                    if (value && Number.isNaN(Number(value))) {
                      return Promise.reject(
                        new Error("Min Commission Earning should be a number")
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input placeholder="e.g. 1000" type="number" />
            </Form.Item>
            <Form.Item
              style={{ flex: 1 }}
              name="maxCommissionEarning"
              label="Max"
              rules={[
                {
                  validator: (_, value) => {
                    if (value && Number.isNaN(Number(value))) {
                      return Promise.reject(
                        new Error("Max Commission Earning should be a number")
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input placeholder="e.g. 1000" type="number" />
            </Form.Item>
          </Row>

          <Typography>Total Payout</Typography>
          <Row style={{ gap: "16px" }}>
            <Form.Item
              style={{ flex: 1 }}
              name="minTotalPayout"
              label="Min"
              rules={[
                {
                  validator: (_, value) => {
                    if (value && Number.isNaN(Number(value))) {
                      return Promise.reject(
                        new Error("Min Total Payout should be a number")
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input placeholder="e.g. 1000" type="number" />
            </Form.Item>
            <Form.Item
              style={{ flex: 1 }}
              name="maxTotalPayout"
              label="Max"
              rules={[
                {
                  validator: (_, value) => {
                    if (value && Number.isNaN(Number(value))) {
                      return Promise.reject(
                        new Error("Max Total Payout should be a number")
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input placeholder="e.g. 1000" type="number" />
            </Form.Item>
          </Row>

          <Typography>Pending Commission</Typography>
          <Row style={{ gap: "16px" }}>
            <Form.Item
              style={{ flex: 1 }}
              name="minPendingCommission"
              label="Min"
              rules={[
                {
                  validator: (_, value) => {
                    if (value && Number.isNaN(Number(value))) {
                      return Promise.reject(
                        new Error("Min Pending Commission should be a number")
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input placeholder="e.g. 1000" type="number" />
            </Form.Item>
            <Form.Item
              style={{ flex: 1 }}
              name="maxPendingCommission"
              label="Max"
              rules={[
                {
                  validator: (_, value) => {
                    if (value && Number.isNaN(Number(value))) {
                      return Promise.reject(
                        new Error("Max Pending Commission should be a number")
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input placeholder="e.g. 1000" type="number" />
            </Form.Item>
          </Row>
        </Form>
      </Modal>

      <Modal
        title="Manage Payout"
        visible={payoutModalVisible}
        onCancel={() => {
          setPayoutModalVisible(false);
        }}
        destroyOnClose
        footer={[
          <Button
            key="submit"
            type="primary"
            loading={loading}
            onClick={() => {
              payoutForm.validateFields().then((values) => {
                // find if oneTimeFee or subscriptionFee is present
                // if yes then convert it to number

                const validatedValues = values;

                if (validatedValues.period) {
                  // add fields startDate and endDate to period
                  validatedValues.period = {
                    startDate: validatedValues.period[0]
                      .startOf("day")
                      .toISOString(),
                    endDate: validatedValues.period[1]
                      .endOf("day")
                      .toISOString(),
                  };
                }

                if (validatedValues.releaseDate) {
                  validatedValues.releaseDate = validatedValues.releaseDate
                    .startOf("day")
                    .toISOString();
                }

                if (validatedValues.amount) {
                  validatedValues.amount = Number(validatedValues.amount);
                }

                handleCreateRefernEarnPayoutForPartner(
                  validatedValues,
                  currentUserForPayout?._id
                );
              });
            }}
            style={{ width: "100%" }}
          >
            Create
          </Button>,
        ]}
      >
        {currentUserForPayout ? (
          <Card style={{ marginBottom: "16px" }}>
            <Space size="small" direction="vertical">
              <Typography style={{}}>{currentUserForPayout?.name}</Typography>
              <Typography>{currentUserForPayout?.email}</Typography>
              <Typography>{currentUserForPayout?.phone}</Typography>
              <Typography>({currentUserForPayout?.host})</Typography>
            </Space>
          </Card>
        ) : null}

        <Form form={payoutForm} layout="vertical">
          <Row style={{ gap: "16px" }}>
            <Form.Item
              style={{ flex: 1 }}
              name="amount"
              label="Payout amount"
              rules={[
                {
                  required: true,
                  message: "Please enter payout amount",
                },
                {
                  validator: (_, value) => {
                    if (value && Number.isNaN(Number(value))) {
                      return Promise.reject(
                        new Error("Payout amount should be a number")
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input placeholder="e.g. 1000" type="number" />
            </Form.Item>

            <Form.Item
              style={{ flex: 1 }}
              name="transactionType"
              label="Transaction Type"
              rules={[
                { required: true, message: "Please select transaction type" },
              ]}
            >
              <Select>
                <Select.Option value="credit">Credit</Select.Option>
                <Select.Option value="debit">Debit</Select.Option>
              </Select>
            </Form.Item>
          </Row>

          <Row style={{ gap: "16px" }}>
            <Form.Item
              style={{ flex: 1 }}
              name="period"
              label="Payout Period"
              rules={[
                {
                  required: true,
                  message: "Please select payout period",
                },
              ]}
            >
              <DatePicker.RangePicker style={{ width: "100%" }} />
            </Form.Item>

            <Form.Item
              noStyle
              shouldUpdate={(prev, curr) =>
                prev.transactionType !== curr.transactionType
              }
            >
              {({ getFieldValue }) => {
                const isCredit = getFieldValue("transactionType") === "credit";
                return (
                  <Form.Item
                    style={{ flex: 1 }}
                    name="releaseDate"
                    label="Release Date"
                    rules={[
                      {
                        required: !isCredit,
                        message: "Please select release date",
                      },
                    ]}
                  >
                    <DatePicker style={{ width: "100%" }} />
                  </Form.Item>
                );
              }}
            </Form.Item>
          </Row>

          <Form.Item name="remark" label="Remark">
            <Input.TextArea placeholder="e.g. Payout for the month of May" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

PartnersList.propTypes = {
  setCurrentScreen: PropTypes.func.isRequired,
  screenData: PropTypes.shape({}),
};

export default PartnersList;
